import React from "react";
import { makeGet, makeDelete } from "@culturehq/client";
import { Button, Loader, Pagination, Table, Warning } from "@culturehq/components";

import { useFetch, usePagination } from "app/hooks";
import { SkillLink } from "app/links";
import { t, Trans } from "app/utils";

import PageHeader from "components/display/PageHeader";

import ChqModalConfirmDelete from "components/common/ChqModalConfirmDelete";
import { createEntity, updateEntity, deleteEntity } from "./mutations";
import SkillModal from "./skills/SkillModal";

const createSkill = createEntity("skills");
const updateSkill = updateEntity("skills");
const deleteSkill = deleteEntity("skills");

const SkillRow = ({ skill, onUpdate, onDelete }) => {
  const onAccept = async () => {
    await makeDelete(`/skills/${skill.id}`);
    onDelete(skill);
  };

  return (
    <tr>
      <td>
        <SkillLink skill={skill} />
      </td>
      <td className="Actions-column">
        <SkillModal skill={skill} onUpdate={onUpdate}>
          {onTrigger => (
            <Button icon="edit-outlined" onClick={onTrigger} inverted />
          )}
        </SkillModal>
        <ChqModalConfirmDelete
          onAccept={onAccept}
          trigger={onTrigger => (
            <Button icon="trash-a-outlined" onClick={onTrigger} inverted />
          )}
        >
          <Trans>Are you sure you would like to delete this skill?</Trans>
        </ChqModalConfirmDelete>
      </td>
    </tr>
  );
};

const Skills = () => {
  const [page, onPageClick] = usePagination();
  const { fetching, json: { skills = [], pagination = {} }, onUpdate: onSkillsUpdate } = useFetch(
    () => makeGet("/skills", { page: page || 1 }),
    [page]
  );

  const onCreate = skill => onSkillsUpdate(createSkill(skill));
  const onUpdate = skill => onSkillsUpdate(updateSkill(skill));
  const onDelete = skill => onSkillsUpdate(deleteSkill(skill));

  return (
    <div className="padding-left-15px padding-right-15px">
      <PageHeader title={t("Manage Skills")}>
        <Trans>
          The below is a list of skills that users and admins have created. They
          will appear in a preset list for users to choose from when editing
          their profile pages.
        </Trans>
      </PageHeader>

      <SkillModal onCreate={onCreate}>
        {onTrigger => (
          <Button icon="plus" primary onClick={onTrigger}>
            <Trans>Add a Skill</Trans>
          </Button>
        )}
      </SkillModal>

      <Loader loading={fetching}>
        <Table>
          <thead>
            <tr>
              <th>
                <Trans>Name</Trans>
              </th>
              <th className="actions">
                <Trans>Actions</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {skills.map(skill => (
              <SkillRow
                key={skill.id}
                skill={skill}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            ))}
          </tbody>
        </Table>
        {skills.length < 1 && (
          <Warning>
            <Trans>
              <strong>Uh oh!</strong> No results to show.
            </Trans>
          </Warning>
        )}
        <Pagination {...pagination} onClick={onPageClick} />
      </Loader>
    </div>
  );
};

export default Skills;
